import type { UnresolvedCommitDescriptor } from 'custom-types/UnresolvedCommitDescriptor';
import type { JSX } from 'react';
import { Icon } from 'ts/components/Icon';
import { Popup } from 'ts/components/Popup';
import { TeamscaleLink } from 'ts/base/routing/TeamscaleLink';
import { getColorForTestExecution } from 'ts/commons/components/TestExecutionResultLabel';
import { Links } from 'ts/commons/links/Links';
import { TestExecutionResultsTable } from 'ts/perspectives/requirement_tracing/views/TestExecutionResultsTable';
import { ETestExecutionResult } from 'typedefs/ETestExecutionResult';
import type { TestPathExecutionWrapper } from 'typedefs/TestPathExecutionWrapper';

type TestInfoPopupProps = {
	project: string;
	commit: UnresolvedCommitDescriptor;
	testPathWrapper: TestPathExecutionWrapper;
};

/** The icon and associated menu for a detected test case in code. */
export function TestInfoPopup({ testPathWrapper, project, commit }: TestInfoPopupProps): JSX.Element {
	return (
		<Popup
			hoverable
			className="max-w-[600px]"
			position="bottom left"
			mouseEnterDelay={500}
			trigger={getIconForTest(testPathWrapper)}
		>
			<p>
				<strong>Test implementation</strong>
				<TestExecutionResultsTable
					testExecutionResults={testPathWrapper.testExecutionResults}
					emptyMessage="There are no test results available for this test at the moment. You can upload test executions to Teamscale to see the results here. "
				/>
			</p>
			<p>
				<TeamscaleLink to={Links.testExecutionOrImplementation(project, testPathWrapper.testPath, commit)}>
					View test execution details
				</TeamscaleLink>
			</p>
		</Popup>
	);
}

function getIconForTest(testExecution: TestPathExecutionWrapper): JSX.Element {
	if (testExecution.testExecutionResults.length === 0) {
		return <Icon color="grey" name="play" />;
	}
	const color = getColorForTestExecution(testExecution.testExecutionResults[0]!.result);
	switch (testExecution.testExecutionResults[0]!.result) {
		case ETestExecutionResult.PASSED.name:
			return <Icon color={color} name="check square" />;
		case ETestExecutionResult.ERROR.name:
		case ETestExecutionResult.FAILURE.name:
			return <Icon color={color} name="times circle" />;
		case ETestExecutionResult.IGNORED.name:
			return <Icon color={color} name="eye slash" />;
		case ETestExecutionResult.INCONCLUSIVE.name:
			return <Icon color={color} name="balance scale" />;
		case ETestExecutionResult.SKIPPED.name:
			return <Icon color={color} name="angle double right" />;
		default:
			return <Icon color={color} name="play" />;
	}
}
